import React, { useState } from 'react';
import { submitContact, useSiteSettings } from '@/lib/siteContent';
import { CheckCircle2, Phone, Mail, MapPin, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Label + field pair, so every input shares spacing, label style and focus ring.
const Field = ({ id, label, optional, children }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-700">
      {label}
      {optional && <span className="ml-1 font-normal text-ink-500">(optional)</span>}
    </label>
    {children}
  </div>
);

const ContactPage = () => {
  const settings = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      // Lands in LK Admin → Customers → Contact Inbox (it used to be a fake setTimeout).
      await submitContact({
        name: formData.name,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        subject: formData.subject || undefined,
        message: formData.message,
      });
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setIsSubmitted(false), 5000); // Reset submitted state after 5 seconds
    } catch (err) {
      setSubmitError(err?.response?.data?.message || 'Could not send your message. Please call or email us instead.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactRows = [
    {
      icon: Phone,
      label: 'Call us',
      value: settings.support_phone.replace(/^\+91/, '+91 '),
      href: `tel:${settings.support_phone.replace(/\s/g, '')}`,
    },
    { icon: Mail, label: 'Email', value: settings.support_email, href: `mailto:${settings.support_email}` },
    { icon: MapPin, label: 'Office', value: settings.office_address },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-surface-muted">
      <Header />
      <main id="main" className="flex-1 py-10 sm:py-16">
        <div className="lk-container">
          <div className="mb-8 max-w-2xl animate-floatUp sm:mb-10">
            <p className="lk-eyebrow mb-2">Contact</p>
            <h1 className="text-h1 text-ink-900">Get in touch</h1>
            <p className="mt-3 text-base leading-relaxed text-ink-500">
              Questions about an event, a booking or listing your venue on LocalKonnect? Send us a
              note and the team will get back to you within a day.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-5 lg:gap-8">
            {/* Contact Information */}
            <aside className="lk-card h-fit p-6 sm:p-8 lg:col-span-2">
              <h2 className="text-h3 text-ink-900">Reach us directly</h2>
              <p className="mt-1 text-sm text-ink-500">We reply within 24 hours.</p>
              <ul className="mt-6 space-y-2">
                {contactRows.map(({ icon: Icon, label, value, href }) => {
                  const body = (
                    <>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-tint">
                        <Icon className="h-5 w-5 text-brand-secondary" aria-hidden="true" />
                      </span>
                      <span className="min-w-0">
                        <span className="lk-label block">{label}</span>
                        <span className="block whitespace-pre-line break-words text-[15px] font-semibold text-ink-900">
                          {value}
                        </span>
                      </span>
                    </>
                  );
                  return (
                    <li key={label}>
                      {href ? (
                        <a href={href} className="lk-focus -mx-2 flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-surface-muted">
                          {body}
                        </a>
                      ) : (
                        <div className="-mx-2 flex items-center gap-3 p-2">{body}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Contact Form */}
            <div className="lk-card p-6 sm:p-8 lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="name" label="Full name">
                    <input type="text" name="name" id="name" autoComplete="name" required value={formData.name} onChange={handleChange} className="lk-input" />
                  </Field>
                  <Field id="email" label="Email address">
                    <input type="email" name="email" id="email" autoComplete="email" required value={formData.email} onChange={handleChange} className="lk-input" />
                  </Field>
                  <Field id="phone" label="Phone">
                    <input type="tel" name="phone" id="phone" autoComplete="tel" inputMode="tel" required value={formData.phone} onChange={handleChange} placeholder="+91 98765 43210" className="lk-input" />
                  </Field>
                  <Field id="subject" label="Subject">
                    <input type="text" name="subject" id="subject" required value={formData.subject} onChange={handleChange} className="lk-input" />
                  </Field>
                </div>
                <Field id="message" label="Message">
                  <textarea name="message" id="message" rows="5" required value={formData.message} onChange={handleChange} className="lk-input resize-y" />
                </Field>
                {submitError && (
                  <div role="alert" className="rounded-xl border border-danger/20 bg-danger-tint p-3 text-sm text-danger">
                    {submitError}
                  </div>
                )}
                {isSubmitted && (
                  <div role="status" className="flex items-center gap-2 rounded-xl border border-success/20 bg-success-tint p-3 text-sm font-medium text-success">
                    <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Thanks — your message has been sent successfully. We&apos;ll be in touch soon.
                  </div>
                )}
                <button type="submit" disabled={isSubmitting} className="lk-btn-primary w-full sm:w-auto">
                  {isSubmitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" aria-hidden="true" />
                      Send message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
